import draftToHtml from 'draftjs-to-html';
import {
  EditorState,
  ContentState,
  convertFromHTML,
  convertToRaw,
} from 'draft-js';
import axios from 'axios';
import moment from 'moment';
import { getImageHash } from '../../../util/functions';
import t from '../../../util/translation/translation';

// A number as typed (spaces, a decimal comma) as the plain number string a
// count up saves, e.g. «12 500,5» as «12500.5»; null when it isn't one
export const parseCount = (value) => {
  if (value === null || value === undefined) return null;
  const cleaned = String(value)
    .replace(/[\s\u00a0]/g, '')
    .replace(',', '.');
  return /^-?\d+(\.\d+)?$/.test(cleaned) ? cleaned : null;
};

// A count down's date: typed in local time (datetime-local), saved as ISO
const DATE_INPUT = 'YYYY-MM-DDTHH:mm';

export const parseField = async (fieldId, value, order, isSubmit = false) => {
  const fieldBase = fieldId.split('-')[0];
  switch (fieldBase) {
    case 'IMAGE':
      return {
        type: 'IMAGE',
        order,
        value: {
          image: value.croppedBase64 || value.preview,
          author: value.author,
          link: value.link,
          // Only when switched on, so other images keep the same value
          ...(value.fullWidth && { fullWidth: true }),
        },
      };
    case 'BUTTON':
      return {
        type: 'BUTTON',
        order,
        value: {
          text: value.text,
          link: value.link,
        },
      };
    case 'RICH_TEXT': {
      const cont = draftToHtml(convertToRaw(value.getCurrentContent()));
      return {
        type: 'RICH_TEXT',
        order,
        value: {
          text: cont,
        },
      };
    }
    case 'RICH_TEXT2': {
      const cont = value;
      return {
        type: 'RICH_TEXT2',
        order,
        value: {
          text: cont,
        },
      };
    }
    case 'FACT_BOX':
      return {
        type: 'FACT_BOX',
        order,
        value: {
          title: value.title,
          text: value.text,
        },
      };
    case 'COUNT_UP':
      return {
        type: 'COUNT_UP',
        order,
        value: {
          value: parseCount(value.value) ?? '',
          prefix: value.prefix || '',
          suffix: value.suffix || '',
          label: value.label || '',
        },
      };
    case 'COUNT_DOWN':
      return {
        type: 'COUNT_DOWN',
        order,
        value: {
          date:
            value.date && moment(value.date).isValid()
              ? moment(value.date).toISOString()
              : '',
          label: value.label || '',
          endText: value.endText || '',
        },
      };
    case 'IMAGE_PHOTOGRAPHER': {
      return {
        type: 'IMAGE_PHOTOGRAPHER',
        order,
        value: {
          text: value,
        },
      };
    }
    case 'IMAGE_CAROUSEL': {
      // A slide's image as the image component sends it (cropped, or the
      // url it already has); slides without one are left out
      const images = (value || [])
        .map((slide) => ({
          image: slide?.image?.croppedBase64 || slide?.image?.preview || '',
          author: slide?.author || '',
          link: slide?.link || '',
        }))
        .filter((slide) => slide.image);
      // One image is an image: the image block with more added, or a
      // carousel taken down to one
      return images.length === 1
        ? { type: 'IMAGE', order, value: images[0] }
        : { type: 'IMAGE_CAROUSEL', order, value: { images } };
    }
    case 'I_FRAME': {
      return {
        type: 'I_FRAME',
        order,
        value: {
          iframe: withPageViewport(value.iframe),
          // Only when ticked, so iframes without it keep the same value
          ...(value.fullWidth && { fullWidth: true }),
        },
      };
    }
    case 'YOUTUBE_VIDEO': {
      if (isSubmit) {
        try {
          await axios.get(
            `https://www.youtube.com/oembed?url=http://www.youtube.com/watch?v=${value}&format=json`
          );
        } catch {
          throw new Error(t('NewsForm.youTubeError'));
        }
      }
      return {
        type: 'YOUTUBE_VIDEO',
        order,
        value: {
          video: value,
        },
      };
    }
    case 'SPORTALITY_VIDEO': {
      return {
        type: 'SPORTALITY_VIDEO',
        order,
        value: {
          video: value,
        },
      };
    }
    case 'HEADER': {
      return {
        type: 'HEADER',
        order,
        value: {
          text: value,
        },
      };
    }
    case 'DELIMITER': {
      return {
        type: 'DELIMITER',
        order,
        value: {
          text: value,
        },
      };
    }
    case 'CUSTOM_VIDEO': {
      if (isSubmit) {
        if (value.promise) {
          const publicUrl = await value.promise();
          return {
            type: 'CUSTOM_VIDEO',
            order,
            value: {
              video: publicUrl,
            },
          };
        }
        return {
          type: 'CUSTOM_VIDEO',
          order,
          value: {
            video: value?.preview || value,
          },
        };
      }
      return {
        type: 'CUSTOM_VIDEO',
        order,
        value: {
          video: value,
        },
      };
    }
    default:
      return null;
  }
};
const isCarousel = (fieldId) => fieldId.startsWith('IMAGE_CAROUSEL');

// An uploaded image as the image field holds it
const imageFieldValue = (url) => ({
  name: 'test name',
  preview: url,
  croppedImage: url,
  hash: getImageHash(url),
});

// Whether a block is kept when the content is saved (blocks with nothing in
// them are left out); the preview uses it to match its parts to the blocks
export const isKeptOnSave = (item, componentValues) =>
  // Checked before images: its id contains IMAGE too
  isCarousel(item.id)
    ? componentValues[`${item.id}_slides`]?.length > 0
    : !!componentValues[item.id] ||
      // A fact box with only a title
      !!componentValues[`${item.id}_title`] ||
      item.name.includes('delimiter') ||
      item.id.toLowerCase().includes('image') ||
      item.id.toLowerCase().includes('button');

const plainText = (html) =>
  typeof html === 'string'
    ? html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim()
    : '';

const hasImage = (image) =>
  !!(image?.croppedBase64 || image?.preview || image?.croppedImage);

// A block nothing has been filled in for yet (warned about before publishing)
export const isBlockEmpty = (blockId, values) => {
  const value = values[blockId];
  switch (blockId.split('-')[0]) {
    case 'DELIMITER':
      return false;
    case 'IMAGE':
      return !hasImage(values[`${blockId}_image`]);
    case 'IMAGE_CAROUSEL':
      return !(values[`${blockId}_slides`] || []).some((slide) =>
        hasImage(slide?.image)
      );
    case 'BUTTON':
      return !values[`${blockId}_link`] || !values[`${blockId}_text`];
    case 'FACT_BOX':
      return !values[`${blockId}_title`] && !plainText(value);
    case 'COUNT_UP':
      return parseCount(value) === null;
    case 'COUNT_DOWN':
      return !value || !moment(value).isValid();
    case 'RICH_TEXT2':
      return !plainText(value) && !/<img/i.test(value || '');
    case 'RICH_TEXT':
      // The old editor keeps a Draft.js editor state
      return typeof value?.getCurrentContent === 'function'
        ? !value.getCurrentContent().hasText()
        : !plainText(value);
    default:
      return typeof value === 'string' ? !value.trim() : !value;
  }
};

// A YouTube link (watch, youtu.be, shorts, embed) as just the video's id;
// anything else is kept as typed
export const youtubeId = (value) => {
  if (typeof value !== 'string') return value;
  const match = value.match(
    /(?:[?&]v=|youtu\.be\/|\/shorts\/|\/embed\/)([A-Za-z0-9_-]{11})/
  );
  return match ? match[1] : value.trim();
};

export const isYoutubeId = (value) =>
  typeof value === 'string' && /^[A-Za-z0-9_-]{11}$/.test(value);

// A page written with AI (its root is .club-page), rather than embed code
export const isAiPage = (html) =>
  typeof html === 'string' && /class="[^"]*\bclub-page\b/.test(html);

// The phone viewport the content service puts first in an AI page, so the
// app's web view doesn't lay it out 980 px wide; added to pages written
// before it did
const PAGE_VIEWPORT =
  '<meta name="viewport" content="width=device-width, initial-scale=1">\n';
const withPageViewport = (html) =>
  isAiPage(html) && !/<meta name="viewport"/.test(html)
    ? PAGE_VIEWPORT + html.trimStart()
    : html;

// The mark the content service puts in front of an AI page (its
// page-marker.ts): which page it is, and a fingerprint of its HTML as
// written. Read the same way here.
const PAGE_MARKER = /<!-- ai-page:([0-9a-f-]{36}):([0-9a-f]{8}) -->\n?/;

/** A short fingerprint of text: FNV-1a, 32 bits, as 8 hex digits. */
export const fingerprint = (text) => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
};

/**
 * What an AI page's mark tells: the page (its first draft, for its
 * versions) and whether its HTML has been changed by hand since it was
 * written. Null for a page without one (written before marks).
 */
export const aiPageInfo = (html) => {
  const match = typeof html === 'string' && html.match(PAGE_MARKER);
  if (!match) return null;
  const written = html.slice(match.index + match[0].length);
  return { pageId: match[1], handEdited: fingerprint(written) !== match[2] };
};

const shorten = (text, length = 70) =>
  text.length > length ? `${text.slice(0, length - 1)}…` : text;

// What a folded block shows in its header: a line of text and, for images,
// a thumbnail
export const blockSummary = (blockId, values) => {
  const value = values[blockId];
  switch (blockId.split('-')[0]) {
    case 'IMAGE': {
      const image = values[`${blockId}_image`];
      return {
        image: image?.croppedBase64 || image?.preview || null,
        text: values[`${blockId}_author`] || '',
      };
    }
    case 'IMAGE_CAROUSEL': {
      const slides = (values[`${blockId}_slides`] || []).filter((slide) =>
        hasImage(slide?.image)
      );
      return {
        image: slides[0]?.image?.croppedBase64 || slides[0]?.image?.preview,
        count: slides.length,
      };
    }
    case 'BUTTON':
      return {
        text: [values[`${blockId}_text`], values[`${blockId}_link`]]
          .filter(Boolean)
          .join(' · '),
      };
    case 'FACT_BOX':
      return {
        text: shorten(
          [values[`${blockId}_title`], plainText(value)]
            .filter(Boolean)
            .join(' · ')
        ),
      };
    case 'COUNT_UP':
      return {
        text: [
          value
            ? `${values[`${blockId}_prefix`] || ''}${value}${
                values[`${blockId}_suffix`] || ''
              }`
            : '',
          values[`${blockId}_label`],
        ]
          .filter(Boolean)
          .join(' · '),
      };
    case 'COUNT_DOWN':
      return {
        text: [
          value && moment(value).isValid()
            ? moment(value).format('DD.MM.YYYY HH:mm')
            : '',
          values[`${blockId}_label`],
        ]
          .filter(Boolean)
          .join(' · '),
      };
    case 'RICH_TEXT2':
      return { text: shorten(plainText(value)) };
    case 'RICH_TEXT':
      return {
        text: shorten(
          typeof value?.getCurrentContent === 'function'
            ? value.getCurrentContent().getPlainText()
            : plainText(value)
        ),
      };
    case 'I_FRAME': {
      // A page written with AI: its first heading, not its CSS
      if (isAiPage(value)) {
        const heading = value.match(/<h[1-3][^>]*>([\s\S]*?)<\/h[1-3]>/);
        return { text: shorten(heading ? plainText(heading[1]) : '') };
      }
      // The address it embeds says more than the code
      const src = typeof value === 'string' && value.match(/src="([^"]+)"/);
      return { text: shorten(src ? src[1] : plainText(value)) };
    }
    case 'CUSTOM_VIDEO':
      return { text: value?.name || '' };
    case 'DELIMITER':
      return { text: '' };
    default:
      return { text: typeof value === 'string' ? shorten(value) : '' };
  }
};

export const componentsFromState = async (
  selectedItems,
  componentValues,
  isSubmit = false
) => {
  const mappedItemsPromises = selectedItems
    .filter((item) => isKeptOnSave(item, componentValues))
    .map((selectedItem, idx) => {
      if (isCarousel(selectedItem.id)) {
        // Full width only when switched on, as for single images
        const fullWidth = !!componentValues[`${selectedItem.id}_fullWidth`];
        return parseField(
          selectedItem.id,
          componentValues[`${selectedItem.id}_slides`],
          idx,
          isSubmit
        ).then((parsed) =>
          fullWidth
            ? { ...parsed, value: { ...parsed.value, fullWidth: true } }
            : parsed
        );
      }
      if (selectedItem.id.toLowerCase().includes('image')) {
        const imageItem = componentValues[`${selectedItem.id}_image`];
        const authorItem = componentValues[`${selectedItem.id}_author`];
        const linkItem = componentValues[`${selectedItem.id}_link`];
        return parseField(
          selectedItem.id,
          {
            ...imageItem,
            author: authorItem || '',
            link: linkItem || '',
            fullWidth: !!componentValues[`${selectedItem.id}_fullWidth`],
          },
          idx
        );
      }
      if (selectedItem.id.toLowerCase().includes('button')) {
        const textItem = componentValues[`${selectedItem.id}_text`];
        const linkItem = componentValues[`${selectedItem.id}_link`];
        return parseField(
          selectedItem.id,
          { text: textItem || '', link: linkItem || '' },
          idx
        );
      }
      if (selectedItem.id.startsWith('FACT_BOX')) {
        return parseField(
          selectedItem.id,
          {
            title: componentValues[`${selectedItem.id}_title`] || '',
            text: componentValues[selectedItem.id] || '',
          },
          idx
        );
      }
      if (selectedItem.id.startsWith('COUNT_UP')) {
        return parseField(
          selectedItem.id,
          {
            value: componentValues[selectedItem.id],
            prefix: componentValues[`${selectedItem.id}_prefix`],
            suffix: componentValues[`${selectedItem.id}_suffix`],
            label: componentValues[`${selectedItem.id}_label`],
          },
          idx
        );
      }
      if (selectedItem.id.startsWith('COUNT_DOWN')) {
        return parseField(
          selectedItem.id,
          {
            date: componentValues[selectedItem.id],
            label: componentValues[`${selectedItem.id}_label`],
            endText: componentValues[`${selectedItem.id}_endText`],
          },
          idx
        );
      }
      if (selectedItem.id.startsWith('I_FRAME')) {
        return parseField(
          selectedItem.id,
          {
            iframe: componentValues[selectedItem.id],
            fullWidth: !!componentValues[`${selectedItem.id}_fullWidth`],
          },
          idx,
          isSubmit
        );
      }
      const itemValue = componentValues[selectedItem.id];
      return parseField(selectedItem.id, itemValue, idx, isSubmit);
    });
  const mappedItems = await Promise.all(mappedItemsPromises);
  return mappedItems;
};
export const AVAILABLE_COMPONENTS = [
  { name: 'richText2', id: 'RICH_TEXT2' },
  { name: 'richText', id: 'RICH_TEXT' },
  { name: 'factBox', id: 'FACT_BOX' },
  { name: 'image', id: 'IMAGE' },
  { name: 'imageCarousel', id: 'IMAGE_CAROUSEL' },
  // { name: 'imagePhotographer', id: 'IMAGE_PHOTOGRAPHER' },
  { name: 'delimiter', id: 'DELIMITER' },
  { name: 'youtubeVideo', id: 'YOUTUBE_VIDEO' },
  { name: 'sportalityVideo', id: 'SPORTALITY_VIDEO' },
  { name: 'customVideo', id: 'CUSTOM_VIDEO' },
  { name: 'iFrame', id: 'I_FRAME' },
  { name: 'button', id: 'BUTTON' },
  { name: 'countUp', id: 'COUNT_UP' },
  { name: 'countDown', id: 'COUNT_DOWN' },
];
const getInitialComponentState = (fieldId, value, variableName) => {
  const fieldBase = fieldId.split('-')[0];
  switch (fieldBase) {
    case 'IMAGE':
      if (variableName === 'link') {
        return value.link;
      }
      if (variableName === 'author') {
        return value.author;
      }
      return imageFieldValue(value.image);

    case 'BUTTON':
      if (variableName === 'link') {
        return value.link;
      }
      if (variableName === 'text') {
        return value.text;
      }
      return value.text;

    case 'RICH_TEXT': {
      const blocksFromHTML = convertFromHTML(value.text);
      const htmlstate = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );
      return EditorState.createWithContent(htmlstate);
    }
    case 'RICH_TEXT2': {
      return value.text;
    }
    case 'FACT_BOX':
      if (variableName === 'title') {
        return value.title;
      }
      return value.text;
    case 'IMAGE_PHOTOGRAPHER':
      return value.text;
    case 'COUNT_UP':
      return variableName ? value[variableName] || '' : value.value;
    case 'COUNT_DOWN':
      if (variableName) {
        return value[variableName] || '';
      }
      return value.date ? moment(value.date).format(DATE_INPUT) : '';
    case 'YOUTUBE_VIDEO':
      return value.video;
    case 'SPORTALITY_VIDEO':
      return value.video;
    case 'HEADER':
      return value.text;
    case 'DELIMITER':
      return value.text;
    case 'I_FRAME':
      return value.iframe;
    case 'CUSTOM_VIDEO':
      return {
        preview: value.video,
      };
    default:
      return null;
  }
};
export const stateFromComponents = (components) => {
  const preparedComponents = components
    .filter((item) => item.type !== 'IMAGE_PHOTOGRAPHER')
    .sort((a, b) => a.order - b.order)
    .map((item, idx) => {
      const found = AVAILABLE_COMPONENTS.find((avb) => item.type === avb.id);
      return {
        name: found.name,
        id: `${found.id}-${idx}`,
        value: item.value,
      };
    });
  const constructedObject = {};
  for (let i = 0; i < preparedComponents.length; i++) {
    const comp = preparedComponents[i];
    if (isCarousel(comp.id)) {
      constructedObject[`${comp.id}_slides`] = (comp.value.images || []).map(
        (slide) => ({
          image: imageFieldValue(slide.image),
          author: slide.author || '',
          link: slide.link || '',
        })
      );
      constructedObject[`${comp.id}_fullWidth`] = !!comp.value.fullWidth;
    } else if (comp.id.toLowerCase().includes('image')) {
      constructedObject[`${comp.id}_fullWidth`] = !!comp.value.fullWidth;
      constructedObject[`${comp.id}_image`] = getInitialComponentState(
        comp.id,
        comp.value,
        false
      );
      constructedObject[`${comp.id}_author`] = getInitialComponentState(
        comp.id,
        comp.value,
        'author'
      );
      constructedObject[`${comp.id}_link`] = getInitialComponentState(
        comp.id,
        comp.value,
        'link'
      );
    } else if (comp.id.toLowerCase().includes('button')) {
      constructedObject[`${comp.id}_text`] = getInitialComponentState(
        comp.id,
        comp.value,
        'text'
      );
      constructedObject[`${comp.id}_link`] = getInitialComponentState(
        comp.id,
        comp.value,
        'link'
      );
    } else if (comp.id.startsWith('I_FRAME')) {
      constructedObject[comp.id] = getInitialComponentState(
        comp.id,
        comp.value
      );
      constructedObject[`${comp.id}_fullWidth`] = !!comp.value.fullWidth;
    } else if (
      comp.id.startsWith('COUNT_UP') ||
      comp.id.startsWith('COUNT_DOWN')
    ) {
      constructedObject[comp.id] = getInitialComponentState(
        comp.id,
        comp.value
      );
      const fields = comp.id.startsWith('COUNT_UP')
        ? ['prefix', 'suffix', 'label']
        : ['label', 'endText'];
      fields.forEach((field) => {
        constructedObject[`${comp.id}_${field}`] = getInitialComponentState(
          comp.id,
          comp.value,
          field
        );
      });
    } else if (comp.id.startsWith('FACT_BOX')) {
      constructedObject[comp.id] = getInitialComponentState(
        comp.id,
        comp.value
      );
      constructedObject[`${comp.id}_title`] = getInitialComponentState(
        comp.id,
        comp.value,
        'title'
      );
    } else {
      constructedObject[comp.id] = getInitialComponentState(
        comp.id,
        comp.value
      );
    }
  }
  return {
    state: constructedObject,
    items: preparedComponents.map((item) => ({
      name: item.name,
      id: item.id,
    })),
  };
};
export const compareComponents = (oldComponents, newComponents) => {
  if (newComponents.length !== oldComponents.length) {
    return true;
  }
  const componentsDifferent = oldComponents.some((oldComp, index) => {
    const newComp = newComponents[index];
    if (JSON.stringify(oldComp.value) !== JSON.stringify(newComp.value)) {
      return true;
    }
    return false;
  });
  return componentsDifferent;
};
